import React, { useState, useRef, useEffect, useCallback } from 'react';

export interface ListColumnProps {
  readonly listId: string;
  readonly title: string;
  readonly color: string;
  readonly width: number;
  readonly cardCount: number;
  readonly wipLimit: number | null;
  readonly wipEnabled: boolean;
  readonly wipMode: 'soft' | 'hard' | null;
  readonly children?: React.ReactNode;
  readonly draggable?: boolean;
  readonly onDragStart?: () => void;
  readonly onCardDragOver?: (e: React.DragEvent) => void;
  readonly onCardDrop?: (e: React.DragEvent) => void;
  readonly onHeaderDragOver?: (e: React.DragEvent) => void;
  readonly onHeaderDrop?: (e: React.DragEvent) => void;
  readonly onMoveLeft?: () => void;
  readonly onMoveRight?: () => void;
  readonly onArchive?: () => void;
  readonly onRenameList?: (listId: string, newTitle: string) => void;
  readonly isFirst?: boolean;
  readonly isLast?: boolean;
}

/**
 * List column component — renders a board column with header, WIP indicator, and card slots.
 * Styled to sit on a colored board background with a semi-transparent appearance.
 * Uses CSS tokens for colors so the column adapts to the active theme and mode.
 */
export function ListColumn({
  listId,
  title,
  color,
  width,
  cardCount,
  wipLimit,
  wipEnabled,
  wipMode,
  children,
  draggable: isDraggable,
  onDragStart,
  onCardDragOver,
  onCardDrop,
  onHeaderDragOver,
  onHeaderDrop,
  onMoveLeft,
  onMoveRight,
  onArchive,
  onRenameList,
  isFirst,
  isLast,
}: ListColumnProps): React.ReactElement {
  const isOverWip = wipEnabled && wipLimit !== null && cardCount >= wipLimit;
  const hasMenu = onMoveLeft || onMoveRight || onArchive;

  // Inline title editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = useCallback(() => {
    if (!onRenameList) return;
    setEditValue(title);
    setIsEditing(true);
  }, [onRenameList, title]);

  const saveTitle = useCallback(() => {
    const trimmed = editValue.trim();
    setIsEditing(false);
    if (trimmed && trimmed !== title && onRenameList) {
      onRenameList(listId, trimmed);
    }
  }, [editValue, title, onRenameList, listId]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditValue(title);
  }, [title]);

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  }, [saveTitle, cancelEditing]);

  const handleDragStart = (e: React.DragEvent<HTMLElement>) => {
    if (onDragStart) {
      e.stopPropagation(); // Prevent card drag from firing
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/x-list-drag', 'list');
      onDragStart();
    }
  };

  return (
    <section
      aria-label={`List: ${title}`}
      className="flex flex-col rounded-lg bg-card/80 backdrop-blur-sm flex-shrink-0 border border-border/50"
      style={{ width: `${width}px`, minWidth: `${width}px` }}
    >
      <header
        className={`px-3 py-2 rounded-t-lg ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
        draggable={isDraggable}
        onDragStart={handleDragStart}
        onDragOver={onHeaderDragOver}
        onDrop={onHeaderDrop}
      >
        {color && (
          <div className="h-1 -mx-3 -mt-2 rounded-t-lg mb-2" style={{ backgroundColor: color }} />
        )}
        <div className="flex items-center justify-between gap-1">
          {isEditing ? (
            <input
              ref={titleInputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              onBlur={saveTitle}
              className="font-semibold text-sm text-card-foreground truncate flex-1 min-w-0 bg-transparent border border-input rounded px-1 py-0 focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label={`Edit title for list "${title}"`}
            />
          ) : (
            <h3
              className="font-semibold text-sm text-card-foreground truncate flex-1 min-w-0 cursor-pointer rounded px-1 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onDoubleClick={startEditing}
              onKeyDown={(e) => { if (e.key === 'Enter') startEditing(); }}
              tabIndex={onRenameList ? 0 : undefined}
              role={onRenameList ? 'button' : undefined}
              aria-label={onRenameList ? `List title: ${title}. Press Enter or double-click to edit.` : undefined}
            >
              {title}
            </h3>
          )}
          <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5 flex-shrink-0">{cardCount}</span>
          {hasMenu && (
            <ListMenu
              title={title}
              onMoveLeft={(!isFirst && onMoveLeft) || undefined}
              onMoveRight={(!isLast && onMoveRight) || undefined}
              onArchive={onArchive}
            />
          )}
        </div>

        {isOverWip && (
          <WipWarning cardCount={cardCount} limit={wipLimit!} mode={wipMode!} />
        )}
      </header>

      <div
        className="flex-1 overflow-y-auto px-2 pb-2 space-y-2"
        role="list"
        aria-label={`Cards in ${title}`}
        onDragOver={onCardDragOver}
        onDrop={onCardDrop}
      >
        {children}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// List Menu (dropdown from ⋮ button)
// ---------------------------------------------------------------------------

interface ListMenuProps {
  readonly title: string;
  readonly onMoveLeft?: () => void;
  readonly onMoveRight?: () => void;
  readonly onArchive?: () => void;
}

function ListMenu({ title, onMoveLeft, onMoveRight, onArchive }: ListMenuProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-muted-foreground hover:text-card-foreground p-1 rounded hover:bg-muted/50 text-xs"
        aria-label={`List actions for ${title}`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        ⋮
      </button>

      {open && (
        <div
          role="menu"
          aria-label={`Actions for ${title}`}
          className="absolute right-0 top-full mt-1 z-50 min-w-[140px] rounded-md border border-border bg-popover shadow-md py-1"
        >
          {onMoveLeft && (
            <button
              role="menuitem"
              onClick={() => { onMoveLeft(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-popover-foreground hover:bg-muted focus:bg-muted focus:outline-none"
            >
              ◀ Move left
            </button>
          )}
          {onMoveRight && (
            <button
              role="menuitem"
              onClick={() => { onMoveRight(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-popover-foreground hover:bg-muted focus:bg-muted focus:outline-none"
            >
              Move right ▶
            </button>
          )}
          {(onMoveLeft || onMoveRight) && onArchive && (
            <div className="my-1 border-t border-border" role="separator" />
          )}
          {onArchive && (
            <button
              role="menuitem"
              onClick={() => { onArchive(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:outline-none"
            >
              Archive list
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WIP Warning
// ---------------------------------------------------------------------------

function WipWarning({ cardCount, limit, mode }: { cardCount: number; limit: number; mode: 'soft' | 'hard' }): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="mt-1 flex items-center gap-1 text-xs px-2 py-1 rounded bg-warning text-warning-foreground"
    >
      <span aria-hidden="true">⚠</span>
      <span>
        WIP {mode === 'hard' ? 'limit' : 'warning'}: {cardCount}/{limit}
      </span>
    </div>
  );
}
